import * as R from 'rambdax'
import {HasRequiredKeys} from 'type-fest'
import {ApiError} from './exceptions'
import {Info, Infos, infos} from './info'
import {stableJson} from './json'
import {toQueryString} from './utils'
import {logger} from './log'

const log = logger('tizra/client')

type ParamsType<T extends Info> = Parameters<T['paramsFn']>[0]

type ResponseType<T extends Info> = Awaited<ReturnType<T['dataFn']>>

type PathInfo = Omit<Info, 'path'> & {path: Exclude<Info['path'], null>}

export type AsyncClient = {
  [K in keyof Infos]: Infos[K] extends {path: null}
    ? (
        path: string,
        params?: ParamsType<Infos[K]>,
        options?: RequestInit,
      ) => Promise<ResponseType<Infos[K]>>
    : HasRequiredKeys<ParamsType<Infos[K]>> extends true
      ? (
          params: ParamsType<Infos[K]>,
          options?: RequestInit,
        ) => Promise<ResponseType<Infos[K]>>
      : (
          params?: ParamsType<Infos[K]>,
          options?: RequestInit,
        ) => Promise<ResponseType<Infos[K]>>
}

export function asyncClient({
  fetch,
  prefix = '/api/',
}: {
  fetch: typeof window.fetch
  prefix?: string
}): AsyncClient {
  return R.map(<I extends Info>(info: I) => {
    const perform = async (
      _path: PathInfo['path'],
      _params: ParamsType<I>,
      {method = info.method, ..._init}: RequestInit,
    ) => {
      // Resolve path which might be a function.
      const path = R.piped(
        typeof _path === 'function' ? _path(_params) : _path,
        path => (path.startsWith('/') ? path : `${prefix}${path}`),
      )

      // Resolve params by paramsFn (typically converting to kebab-case)
      const params = info.paramsFn(_params)

      // Build the URL and RequestInit depending on the method.
      // The big difference here is that POST/PUT get params converted to body,
      // but GET/DELETE get params appended as query string.
      const [url, init] =
        method === 'POST' || method === 'PUT'
          ? [
              path,
              {
                ..._init,
                method,
                body: stableJson(params),
                headers: {
                  'content-type': 'application/json',
                  ..._init.headers,
                },
              },
            ]
          : [`${path}${toQueryString(params, '?')}`, {..._init, method}]

      // Attempt to fetch. Convert to ApiError if there's a problem.
      const response = await fetch(url, init).catch(error => {
        throw new ApiError({error, url, detail: {body: init.body, method}})
      })

      // Attempt to decode JSON response. This does not throw ApiError because
      // we defer that to isOk to decide.
      const contentType =
        response.headers.get('content-type')?.split(';')[0].toLowerCase() || ''
      const raw =
        contentType === 'application/json'
          ? await response.json().catch(() => {
              log.error(`failed to decode JSON response for ${url}`)
            })
          : undefined // defer to dataFn to interpret response.body

      // Throw ApiError if the response is not okay.
      const {status} = response
      const ok = await info.isOk({
        contentType,
        params,
        data: raw,
        response,
        status,
      })
      if (!ok) {
        const text = await response.text().catch(() => undefined)
        throw new ApiError({
          url,
          response,
          detail: {body: init.body, method, text},
        })
      }

      // Response appears okay, convert the data (to camelCase).
      // info.dataFn can be async or sync, but await works for both.
      let data
      try {
        data = await info.dataFn({
          contentType,
          params,
          data: raw,
          response,
          status,
        })
      } catch (error) {
        throw new ApiError({
          url,
          error,
          response,
          detail: {method, params, raw},
        })
      }

      // It's amazing anything works.
      return data
    }
    return info.path === null
      ? async (
          path: string,
          params: ParamsType<I>,
          options: RequestInit = {},
        ) => await perform(path, params, options)
      : async (params: ParamsType<I>, options: RequestInit = {}) =>
          await perform(info.path!, params, options)
  }, infos) as AsyncClient
}

const fetch = typeof window === 'undefined' ? (undefined as any) : window.fetch

export const api = asyncClient({fetch})

export const admin = asyncClient({fetch, prefix: '/admin-api/_/'})
